import React from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from '../reporting_utils/ErrorBoundary';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import '@reach/tabs/styles.css';
import Dashboard from './Dashboard';
import Trends from './Trends';
import CallAnalysis from './CallAnalysis';
import MarketingBreakdown from './MarketingBreakdown';
import AppointmentConversion from './AppointmentConversion';
import moment from 'moment';
import { Badge, BadgeList } from '../reporting_utils/Badge';

function ScheduledPerformanceSummary({ reportData, locationNames, groupNames, endDate }) {
  const currentMonth = moment(endDate).startOf('month').format('YYYY-MM-DD');

  const currentYear = endDate.slice(0, 4);
  const previousYear = (parseInt(currentYear) - 1).toString();

  const currentMonthConversionRateYou = reportData.results.at(-1).appt_conversion_rate ?? 0;
  const priorMonthConversionRateYou = reportData.results.at(-2)?.appt_conversion_rate ?? 0;
  const sixMonthAverageConversionYou = Math.round(
    reportData.results.slice(-6).reduce((acc, item) => {
      return acc + item.appt_conversion_rate;
    }, 0) / 6
  );

  const sixMonthAvgProspectCallsYou =
    reportData.results.slice(-6).reduce((acc, item) => {
      return acc + item.prospect_calls;
    }, 0) / 6;
  const sixMonthAvgQualifiedCallsYou =
    reportData.results.slice(-6).reduce((acc, item) => {
      return acc + item.leadscore_calls;
    }, 0) / 6;
  const sixMonthAvgProspectRateYou = Math.round(
    (sixMonthAvgProspectCallsYou / sixMonthAvgQualifiedCallsYou) * 100
  );

  let currentMonthConversionRateIndustry = 0;
  let priorMonthConversionRateIndustry = 0;
  let sixMonthAverageConversionIndustry = 0;
  let sixMonthAvgProspectCallsIndustry = 0;
  let sixMonthAvgQualifiedCallsIndustry = 0;
  let sixMonthAvgProspectRateIndustry = 0;

  if (reportData.industry) {
    currentMonthConversionRateIndustry = reportData.industry.at(-1).appt_conversion_rate;
    priorMonthConversionRateIndustry = reportData.industry.at(-2)?.appt_conversion_rate ?? 0;
    sixMonthAverageConversionIndustry = Math.round(
      reportData.industry.slice(-6).reduce((acc, item) => {
        return acc + item.appt_conversion_rate;
      }, 0) / 6
    );

    sixMonthAvgProspectCallsIndustry =
      reportData.industry.slice(-6).reduce((acc, item) => {
        return acc + item.prospect_calls;
      }, 0) / 6;
    sixMonthAvgQualifiedCallsIndustry =
      reportData.industry.slice(-6).reduce((acc, item) => {
        return acc + item.leadscore_calls;
      }, 0) / 6;
    sixMonthAvgProspectRateIndustry = Math.round(
      (sixMonthAvgProspectCallsIndustry / sixMonthAvgQualifiedCallsIndustry) * 100
    );
  }

  return (
    <div className='report-details-container'>
      <div className='report-details-header'>
        <div className='report-details-title'>
          <h1>Performance Summary</h1>
        </div>
      </div>
      <div>
        {groupNames.length > 0 ? (
          <BadgeList title='Selected groups' maxLength={3}>
            {groupNames.map((name) => (
              <Badge label={name} key={name} />
            ))}
          </BadgeList>
        ) : null}
        {locationNames.length > 0 ? (
          <BadgeList title='Selected locations' maxLength={3}>
            {locationNames.map((name) => (
              <Badge label={name} key={name} />
            ))}
          </BadgeList>
        ) : null}
      </div>
      <ErrorBoundary>
        <Tabs>
          <TabList style={{ marginBottom: 20 }}>
            <Tab>Dashboard</Tab>
            <Tab>Monthly & Quarterly Trends</Tab>
            <Tab>Call Analysis</Tab>
            <Tab>Marketing Breakdown</Tab>
            <Tab>Appointment Conversion Breakdown</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <Dashboard
                data={reportData}
                sixMonthAverageConversionYou={sixMonthAverageConversionYou}
                sixMonthAverageConversionIndustry={sixMonthAverageConversionIndustry}
                currentYear={currentYear}
                previousYear={previousYear}
              />
            </TabPanel>
            <TabPanel>
              <Trends data={reportData} currentYear={currentYear} previousYear={previousYear} />
            </TabPanel>
            <TabPanel>
              <CallAnalysis
                data={reportData}
                conversionRates={{
                  currentMonthConversionRateYou,
                  priorMonthConversionRateYou,
                  sixMonthAverageConversionYou,
                  currentMonthConversionRateIndustry,
                  priorMonthConversionRateIndustry,
                  sixMonthAverageConversionIndustry,
                }}
                prospectRates={{
                  sixMonthAvgProspectRateYou,
                  sixMonthAvgProspectRateIndustry,
                }}
                results={reportData.results}
                industry={reportData.industry}
                currentYear={currentYear}
              />
            </TabPanel>
            <TabPanel>
              <MarketingBreakdown tableData={reportData.campaigns} currentMonth={currentMonth} />
            </TabPanel>
            <TabPanel>
              <AppointmentConversion tableData={reportData.employees} currentMonth={currentMonth} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ErrorBoundary>
    </div>
  );
}

ScheduledPerformanceSummary.propTypes = {
  reportData: PropTypes.object.isRequired,
  locationNames: PropTypes.array.isRequired,
  groupNames: PropTypes.array.isRequired,
  endDate: PropTypes.string.isRequired,
};

export default ScheduledPerformanceSummary;
